/**
 * Per-channel notification preference for team-chat (TC-22).
 *
 * Each channel can be set to notify on 'all' messages (default), only on
 * '@mentions', or 'none'. 'all' and 'mentions' are client-side only,
 * persisted in localStorage under one JSON map — same "local until the
 * backend grows an endpoint" approach as the conversation-pins store.
 *
 * Q4 (backend PR #10): 'none' is now SYNCED with the server. The backend
 * models a per-member boolean mute (`ChannelMember.is_muted`, flipped via
 * POST /channels/:id/mute|/unmute) — it does NOT model 'mentions', so that
 * level stays local. The bridge lives in two places:
 *   - WRITE: `useSetChannelNotifyPref` (queries.ts) persists the local pref
 *     AND fires mute/unmute when the pref crosses the 'none' boundary.
 *   - READ: `effectiveNotifyPref` (below) merges the server's is_muted into
 *     the local pref — server mute wins as 'none'; an explicit server
 *     UNMUTE (e.g. from another device) overrides a stale local 'none'.
 *
 * Pure module: no React, no Zustand. The realtime manager reads it
 * synchronously per event (`effectiveNotifyPref` over the channels cache +
 * `shouldNotifyChannelMessage`), and the channel UI writes it via
 * `useSetChannelNotifyPref`/`setChannelNotifyPref`.
 *
 * Storage shape: { [channelId]: 'mentions' | 'none' } — 'all' entries are
 * PRUNED on write (default needs no storage), so the map only holds
 * channels the user explicitly quieted.
 */

export type ChannelNotifyPref = 'all' | 'mentions' | 'none';

export const CHANNEL_NOTIFY_STORAGE_KEY = 'blaze-channel-notify';

/** localStorage, or null when unavailable (SSR, tests without a DOM,
 *  privacy modes that throw on access). All callers degrade to defaults. */
function safeStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    if (typeof localStorage === 'undefined' || localStorage === null) return null;
    return localStorage;
  } catch {
    return null;
  }
}

/** Read the whole map defensively — corrupt JSON / non-object payloads
 *  (someone else wrote to the key) degrade to an empty map, never throw. */
function readMap(): Record<string, unknown> {
  const ls = safeStorage();
  if (!ls) return {};
  try {
    const raw = ls.getItem(CHANNEL_NOTIFY_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return {};
  } catch {
    return {};
  }
}

/**
 * The notification preference for a channel. Defaults to 'all' for unknown
 * channels, unparseable storage, or invalid stored values — being noisy by
 * default is safer than silently muting a channel.
 */
export function getChannelNotifyPref(channelId: string): ChannelNotifyPref {
  if (!channelId) return 'all';
  const value = readMap()[channelId];
  return value === 'mentions' || value === 'none' ? value : 'all';
}

/**
 * Persist a channel's preference. Setting 'all' (the default) removes the
 * entry instead of storing it, keeping the map minimal. No-ops silently when
 * localStorage is unavailable or full (quota) — a lost preference write is
 * strictly better than a crash in a notification path.
 */
export function setChannelNotifyPref(channelId: string, pref: ChannelNotifyPref): void {
  if (!channelId) return;
  const ls = safeStorage();
  if (!ls) return;
  const map = readMap();
  if (pref === 'all') {
    if (!(channelId in map)) return; // nothing stored → nothing to clear
    delete map[channelId];
  } else {
    map[channelId] = pref;
  }
  try {
    ls.setItem(CHANNEL_NOTIFY_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // QuotaExceeded / storage disabled mid-session — drop the write.
  }
}

/**
 * Whether an incoming channel message should notify (sound/desktop) given the
 * channel's preference and whether the message mentions the current user.
 *
 *   'all'      → always
 *   'mentions' → only when it's a mention
 *   'none'     → never (not even mentions — explicit full mute)
 */
export function shouldNotifyChannelMessage(
  pref: ChannelNotifyPref,
  isMention: boolean,
): boolean {
  if (pref === 'none') return false;
  if (pref === 'mentions') return isMention;
  return true;
}

// ---------------------------------------------------------------------------
// Q4: server-mute bridge (backend PR #10).
// ---------------------------------------------------------------------------

/**
 * The slice of a channel this module needs to resolve the SERVER mute state.
 * Structural on purpose (no import from queries.ts — this module stays pure):
 * `ChannelDTO` itself has no top-level `is_muted`; the flag lives on the
 * caller's row inside `members[]` (`ChannelMemberDTO.is_muted`, verified in
 * blazeteams-backend channel_dto.go / channelToDTO). `is_muted` at the top
 * level is accepted too, forward-compatible with the backend ever surfacing
 * a per-viewer scalar.
 */
export interface ChannelMuteView {
  is_muted?: boolean;
  members?: Array<{ user_id?: string; is_muted?: boolean } | null | undefined> | null;
}

/**
 * The server's mute state of a channel for `myUserId`.
 *
 * Resolution: top-level `is_muted` when the backend provides one → otherwise
 * MY member row's `is_muted`. Returns `undefined` when not determinable
 * (no channel, no user id, my member row missing or without the flag) —
 * callers then fall back to purely local behavior.
 */
export function serverChannelMuted(
  channel: ChannelMuteView | null | undefined,
  myUserId?: string | null,
): boolean | undefined {
  if (!channel) return undefined;
  if (typeof channel.is_muted === 'boolean') return channel.is_muted;
  if (!myUserId) return undefined;
  const mine = (channel.members ?? []).find((m) => m?.user_id === myUserId);
  return typeof mine?.is_muted === 'boolean' ? mine.is_muted : undefined;
}

/**
 * Merge the server's is_muted into the local 3-state preference:
 *
 *   server muted           → 'none'      (server mute always wins)
 *   server unmuted + local 'none' → 'all' (unmuted elsewhere overrides the
 *                                          stale local full-mute — 'none' is
 *                                          the synced level, so the server is
 *                                          authoritative for that boundary)
 *   otherwise              → localPref   ('all'/'mentions' stay local; the
 *                                          server doesn't model 'mentions')
 *
 * When the server state is unknown (`undefined`) the local pref rules alone.
 */
export function effectiveNotifyPref(
  channel: ChannelMuteView | null | undefined,
  localPref: ChannelNotifyPref,
  myUserId?: string | null,
): ChannelNotifyPref {
  const muted = serverChannelMuted(channel, myUserId);
  if (muted === true) return 'none';
  if (muted === false && localPref === 'none') return 'all';
  return localPref;
}
